import { Waves, Route, Wind, Satellite, MapPin, RotateCw, Play, Pause, Radio } from "lucide-react";

const LAYERS = [
  { key: "biomass", label: "Biomass Belt", icon: Waves },
  { key: "drift", label: "Drift Paths", icon: Route },
  { key: "flow", label: "Flow Field", icon: Wind },
  { key: "satellite", label: "MODIS Layer", icon: Satellite },
  { key: "islands", label: "Islands", icon: MapPin },
];

export default function ControlBar({
  layers,
  onToggle,
  onRefresh,
  refreshing,
  frames = [],
  idx = 0,
  nowIndex = 0,
  playing,
  onScrub,
  onPlayToggle,
}) {
  const frame = frames[idx];
  const isLive = idx === nowIndex;
  const isForecast = frame?.kind === "forecast";

  return (
    <div className="glass rounded-xl p-3" data-testid="control-bar">
      <div className="flex flex-wrap items-center gap-2">
        {LAYERS.map(({ key, label, icon: Icon }) => {
          const on = layers[key];
          return (
            <button
              key={key}
              onClick={() => onToggle(key)}
              data-testid={`layer-toggle-${key}`}
              className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[11px] font-mono-tel font-semibold uppercase tracking-wider transition-all ${
                on
                  ? "border-cyan-400/50 bg-cyan-500/15 text-cyan-200 shadow-[0_0_12px_rgba(0,229,255,0.25)]"
                  : "border-white/10 bg-white/[0.03] text-slate-500 hover:text-slate-300 hover:border-white/20"
              }`}
            >
              <Icon className="h-3.5 w-3.5" />
              {label}
            </button>
          );
        })}
        <button
          onClick={onRefresh}
          data-testid="refresh-btn"
          className="ml-auto inline-flex items-center gap-1.5 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-3 py-1.5 text-[11px] font-mono-tel font-semibold uppercase tracking-wider text-emerald-300 transition-colors hover:bg-emerald-500/20"
        >
          <RotateCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {/* observed history -> NOW -> ensemble forecast timeline */}
      <div className="mt-3 flex items-center gap-3">
        <button
          onClick={onPlayToggle}
          data-testid="playback-toggle"
          disabled={!frames.length}
          className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-cyan-400/50 bg-cyan-500/15 text-cyan-200 transition-colors hover:bg-cyan-500/25 disabled:opacity-40"
          aria-label={playing ? "Pause playback" : "Play playback"}
        >
          {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4 pl-0.5" />}
        </button>
        <span className="eyebrow hidden shrink-0 text-slate-500 sm:block">
          Observed <span className="text-slate-600">→ NOW →</span> <span className="text-amberx">Forecast</span>
        </span>
        <input
          type="range"
          min={0}
          max={Math.max(0, frames.length - 1)}
          step={1}
          value={idx}
          onChange={(e) => onScrub(Number(e.target.value))}
          className={`tel-range flex-1 ${isForecast ? "tel-range-fc" : ""}`}
          data-testid="playback-scrubber"
        />
        <div className="flex w-36 shrink-0 items-center justify-end gap-2">
          {isLive ? (
            <span className="inline-flex items-center gap-1 font-mono-tel text-xs font-bold text-emerald-300">
              <Radio className="h-3.5 w-3.5" /> LIVE
            </span>
          ) : (
            <span className={`font-mono-tel text-xs font-bold ${isForecast ? "text-amberx" : "text-cyanx"}`}>
              {isForecast ? "FORECAST" : "OBSERVED"}
            </span>
          )}
          <span className="font-mono-tel text-xs text-slate-400" data-testid="playback-date">
            {frame?.label}
          </span>
        </div>
      </div>
    </div>
  );
}
